/**
 * Seeded default overtime types
 * These are pre-configured for every new organisation. Admins can edit, deactivate,
 * or add new types in Settings → Overtime. They are mock/frontend-only until
 * BE-OT-4 and BE-OT-5 land.
 *
 * IDs use the 'ot-seed-' prefix so they're easily identifiable in data/logs.
 */

import type { OvertimeType } from '@/types/timesheetSettings';

export const SEEDED_OVERTIME_TYPES: OvertimeType[] = [
  {
    id: 'ot-seed-1',
    name: 'Time & a Half',
    costMultiplier: 1.5,
    billMultiplier: 1.5,
    badgeColour: '#f59e0b', // amber-400
    isDefault: true,
    active: true,
    createdAt: new Date('2025-01-01'),
  },
  {
    id: 'ot-seed-2',
    name: 'Double Time',
    costMultiplier: 2.0,
    billMultiplier: 2.0,
    badgeColour: '#ef4444', // red-500
    isDefault: false,
    active: true,
    createdAt: new Date('2025-01-01'),
  },
  {
    id: 'ot-seed-3',
    name: 'Night Shift',
    costMultiplier: 1.25,
    billMultiplier: 1.25,
    badgeColour: '#8b5cf6', // violet-500
    isDefault: false,
    active: true,
    createdAt: new Date('2025-01-01'),
  },
];

/** Pre-built ID → type lookup for fast access in mappers and tests */
export const SEEDED_OVERTIME_TYPES_BY_ID: Record<string, OvertimeType> =
  Object.fromEntries(SEEDED_OVERTIME_TYPES.map((t) => [t.id, t]));

/**
 * Returns the org default seeded overtime type.
 * Throws if the invariant is ever violated (should never happen with seed data).
 */
export function getDefaultSeededOvertimeType(): OvertimeType {
  const defaultType = SEEDED_OVERTIME_TYPES.find((t) => t.isDefault);
  if (!defaultType) {
    throw new Error('Invariant violation: no default overtime type found in seed data');
  }
  return defaultType;
}
